// @ts-strict-ignore
import {Injectable} from "@angular/core";
import {FunctionNames, NetworkInterface, PersistedClientData, StaticHostData, StorageKeys, WifiData} from "@models";
import {BehaviorSubject, Observable, Subject} from "rxjs";
//THIS HAS TO BE AN IMPORT in long form and not SHORTENED FORM!!!
// noinspection ES6PreferShortImport
import {SimClientStorageService} from "./sim-client-storage.service";
import {SimUtilityService} from "./sim-utility.service";

@Injectable({
	providedIn: "root",
})
export class SimInjectableDataService {
	private clientWifiSubject = new BehaviorSubject<WifiData>(null);
	public networkInterfacesChanged = new Subject<void>();

	constructor(private clientStorageService: SimClientStorageService) {
		window[FunctionNames.WifiConnection] = (data: WifiData) => {
			this.clientWifiSubject.next(data);
		};
		window[FunctionNames.NetworkInterfaces] = (data: NetworkInterface[]) => {
			this.networkInterfaces = data;
		};

		window["setNetwork"] = (mac: string, ip: string) => {
			this.setNetwork(mac, ip);
		};

		window["setDeviceId"] = (deviceId: string) => {
			this.setDeviceId(deviceId);
		};

		this.clientIdMigration();
	}

	public get hostData() {
		return this.getInject<StaticHostData>(StorageKeys.StaticHostData, false);
	}

	private set hostData(data: StaticHostData) {
		this.setSessionInject(StorageKeys.StaticHostData, data);
	}

	public get networkInterfaces() {
		return this.getInject<NetworkInterface[]>(StorageKeys.NetworkInterfaces, true);
	}

	public set networkInterfaces(data: NetworkInterface[]) {
		const currentValue = this.networkInterfaces;
		this.setSessionInject(StorageKeys.NetworkInterfaces, data);

		if (!SimUtilityService.arraysAreIdentical(currentValue, data)) {
			this.networkInterfacesChanged.next();
		}
	}

	public get clientData() {
		return this.getInject<PersistedClientData>(StorageKeys.PersistedClientData, false);
	}

	private set clientData(data: PersistedClientData) {
		this.setSessionInject(StorageKeys.PersistedClientData, data);
		this.setLocalInject(StorageKeys.PersistedClientData, data);

		if (window.electron) {
			window.electron.storeClientData(data);
		} else {
			//If run in a browser environment, store the data in local storage
			console.warn("window.electron is undefined! Client is not running in an electron environment");
		}
	}

	public getClientWifi(): Observable<WifiData | null> {
		return this.clientWifiSubject;
	}

	//**************************************************************************
	// Helper functions
	//**************************************************************************

	private getInject<T = never>(key: string, isArray: boolean): T {
		//SessionStorage first, LocalStorage as fallback
		const data = this.clientStorageService.getSessionData(key) ?? this.clientStorageService.getLocalData(key);
		const parsed = JSON.parse(data) as T;
		return parsed ?? ((isArray ? [] : {}) as T);
	}

	private setSessionInject<T = never>(key: string, data: T) {
		this.clientStorageService.setSessionData(key, JSON.stringify(data));
	}

	private setLocalInject<T = never>(key: string, data: T) {
		this.clientStorageService.setLocalData(key, JSON.stringify(data));
	}

	private setNetwork(mac: string, ip: string) {
		this.networkInterfaces = [{mac: mac, address: ip, family: null, adapter: null, netmask: null}];
	}

	public setDeviceId(deviceId: string) {
		const data = this.clientData;
		data.deviceId = deviceId;
		this.clientData = data;
	}

	//**************************************************************************
	// Backwards Compatibility Shim
	//**************************************************************************

	public get mac() {
		return this.networkInterfaces?.[0]?.mac ?? "";
	}

	private clientIdMigration() {
		const data = JSON.parse(this.clientStorageService.getLocalData("static-injection")) as {clientId: string};
		if (!!data?.clientId) {
			this.setDeviceId(data.clientId);
			this.clientStorageService.removeLocalData("static-injection");
		}
	}
}
